import { dispatcher } from 'dispatcher';
import { useMemo } from 'react';
import { useChangePlayerFavorites, useGetPlayerFavorites } from 'server';

import * as hooks from './hooks';

export const useGamePublicAPI = () => {
    const getGamesConfiguration = hooks.useGetGamesConfiguration();
    const getPlayerFavorites = useGetPlayerFavorites();
    const changePlayerFavorites = useChangePlayerFavorites();

    return useMemo(() => ({
        /**
         * Open a game according to launch configuration of its game type, if it exists.
         * Otherwise, do nothing.
         * @memberOf Playtech.API.game
         * @param params {Object}
         * @param params.code {String} game code that is configured in CMS
         * @param [params.mode=1] {Number} 1 - real mode, 0 - demo mode
         * @example
         * Playtech.API.game.open({ code: 'gameCode' });
         */
        open: ({ code, mode = 1 }) => dispatcher.dispatch('game:launch', { code, mode }),

        /**
         * Getting all games data.
         * @memberOf Playtech.API.game
         * @param [disableCache] {Boolean} if true data will be always taken from server instead of storage
         * @example
         * <!--
         *  {
         *      games: {"Marine Mayhem 1": {active:true,gameCode:"Multi Balls",mobileGameCode:"Multi Balls",images:{base:"/library/games_images/Bingo/multiBalls.png",guest:""},name:"Multi Balls",description:"",funSupport:"unavailable","ackpotCode:"",excludeFromPGG2:false,gameType: "BINGOROOM",seoFriendlyGameName:"multi-balls",articleId:"",isNew:false,hot:false,popular:false,exclusive:false}, ...},
         *      gameTypes: [{demoPlayUrl: "NGM2DTDEMO", desktop: true, launchMethodDesktop: "wrapperInThisWindow", launchMethodMobile: "redirectInThisWindow", mobile: true, name: "NGM2DT", previewDemoPlayUrl: "", previewRealPlayUrl: "", realPlayUrl: "3rdtest_ngm_real"}, ...],
         *      gamesArticles: {GAME-3-CARD-BRAG: "", GAME-3D-ROULETTE: "", GAME-8BALL: "", GAME-ADVENTURE: "", ...},
         *      gamesConfiguration: {"Bingo Bowl - Virtue Bingo": {gameCode: "Bingo Bowl - Virtue Bingo", active: true, ...}, ...},
         *      gamesFeeds: [{active: true, name: 'name', categories: [{cid: "93d0ab81-78fe-1a06-9980-07894efeee9b", displayName: "playtech.mobile.game-categories.eeee.qewe.long", displayNameShort: "playtech.mobile.game-categories.eeee.qewe.short", gameCodes: [], name: "qewe", pgg2: {enabled: false, maxGames: 0, minGames: 0}, roundIcon: "" }, ...]}, ...],
         *      gamesProviders: {},
         *      gamesTilesLayouts: {layouts: [], list: ["Wszystkie", "Wszystkie-No-branded", ...]}
         *  }
         *  -->
         *  Playtech.API.game.getGamesConfiguration().then(console.log);
         * @returns {Promise<Object>}
         */
         getGamesConfiguration,
        /**
         * Return player favorites.
         * @memberOf Playtech.API.game
         * @param params {Object}
         * @param params.requestedFavorites {String[]} list of supported favorites. only 'games' is supported for now.
         * @example
         * Playtech.API.game.getPlayerFavorites({ requestedFavorites: ['games'] });
         */
        getPlayerFavorites,
        /**
         * Change player favorites.
         * @memberOf Playtech.API.game
         * @param params {Object}
         * @param params.requestType {"add" | "remove"} type of the operation.
         * @param params.gameTypes {String[]} list of game codes to add to / remove from favorites.
         * @example
         * Playtech.API.game.changePlayerFavorites({ gameTypes: ['manos'], requestType: 'add' });
         */
        changePlayerFavorites
    }), [
        getPlayerFavorites,
        changePlayerFavorites,
        getGamesConfiguration
    ]);
};
